import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../services/api';
import { FormSkeleton } from '../../components/skeletons';

const S = {
  page: { padding: 24, background: 'var(--cream)', minHeight: '100vh' },
  title: { fontFamily: 'var(--font-serif)', fontSize: 28, fontWeight: 700, color: 'var(--char)', marginBottom: 4 },
  subtitle: { fontSize: 13, color: 'var(--muted)', marginBottom: 24 },
  card: { background: '#fff', border: '1px solid var(--border)', borderRadius: 16, padding: 28, maxWidth: 650, boxShadow: '0 4px 12px rgba(0,0,0,0.03)' },
  sectionTitle: { fontSize: 16, fontWeight: 700, color: 'var(--char)', marginBottom: 18, borderBottom: '1px solid var(--border)', paddingBottom: 10 },
  row: { marginBottom: 20 },
  label: { fontSize: 12, fontWeight: 700, color: 'var(--char)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, display: 'block' },
  select: { width: '100%', padding: '12px 14px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 14, background: '#fff', cursor: 'pointer', outline: 'none', transition: 'border-color 0.2s' },
  input: { width: '100%', padding: '12px 14px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 14, outline: 'none', transition: 'border-color 0.2s' },
  helpText: { fontSize: 12, color: 'var(--muted)', marginTop: 6, lineHeight: 1.4 },
  btn: { background: '#4C1D95', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', transition: 'opacity 0.2s, transform 0.1s' },
  alert: { padding: '14px 18px', borderRadius: 10, marginBottom: 20, fontSize: 13, display: 'flex', alignItems: 'center', gap: 10, fontWeight: 500 },
};

export default function SystemSettings() {
  const [settings, setSettings] = useState({
    system_mode: 'live_production',
    free_credit_amount: 500,
    kyc_platform_fee: 500,
    min_wallet_recharge: 500,
    request_credit_threshold: 10,
    auto_credit_amount: 300
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    adminAPI.getSettings()
      .then(res => {
        const data = res.data.data || [];
        const config = {};
        data.forEach(item => {
          config[item.key] = item.value;
        });
        setSettings(prev => ({ ...prev, ...config }));
      })
      .catch(err => {
        console.error('Failed to load settings:', err);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setToast(null);
    try {
      const payload = {
        system_mode: settings.system_mode,
      };

      if (settings.system_mode === 'local_production') {
        payload.free_credit_amount = Number(settings.free_credit_amount);
        payload.request_credit_threshold = Number(settings.request_credit_threshold);
        payload.auto_credit_amount = Number(settings.auto_credit_amount);
      } else {
        payload.kyc_platform_fee = Number(settings.kyc_platform_fee);
        payload.min_wallet_recharge = Number(settings.min_wallet_recharge);
      }

      await adminAPI.updateSettings(payload);
      setToast({ type: 'success', message: 'System configurations updated successfully!' });
      setTimeout(() => setToast(null), 4000);
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.message || 'Failed to save settings.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <FormSkeleton fields={5} style={{ margin: '24px 20px' }} />;
  }

  const isLocalMode = settings.system_mode === 'local_production';

  return (
    <div style={S.page}>
      <div style={S.title}>System Settings</div>
      <div style={S.subtitle}>Configure global system operations, user flow behaviors, and pricing limits.</div>

      {toast && (
        <div style={{
          ...S.alert,
          background: toast.type === 'success' ? '#ECFDF5' : '#FEF2F2',
          color: toast.type === 'success' ? '#065F46' : '#991B1B',
          border: `1.5px solid ${toast.type === 'success' ? '#A7F3D0' : '#FCA5A5'}`
        }}>
          <span>{toast.type === 'success' ? '✅' : '⚠️'}</span>
          <span>{toast.message}</span>
        </div>
      )}

      <div style={S.card}>
        <form onSubmit={handleSave}>
          <div style={S.sectionTitle}>⚙️ Global System Operations</div>

          {/* Mode Switcher */}
          <div style={S.row}>
            <label style={S.label}>System Operational Mode</label>
            <select
              style={{
                ...S.select,
                borderColor: isLocalMode ? '#F5A623' : '#4C1D95',
                boxShadow: isLocalMode ? '0 0 8px rgba(245, 166, 35, 0.15)' : 'none'
              }}
              value={settings.system_mode}
              onChange={e => setSettings({ ...settings, system_mode: e.target.value })}
            >
              <option value="live_production">Live Production (Standard Mode)</option>
              <option value="local_production">Local Production (Sandbox / Testing Mode)</option>
            </select>
            <div style={S.helpText}>
              {isLocalMode ? (
                <span style={{ color: '#D97706', fontWeight: 600 }}>
                  ⚠️ Sandbox Mode Enabled: Bypasses vendor KYC review, sets minimum wallet recharge to ₹1, and grants free signup credits. Used for frictionless local and staging testing.
                </span>
              ) : (
                <span style={{ color: '#15803D', fontWeight: 600 }}>
                  🛡️ Strict Production Mode: Enforces mandatory vendor KYC approvals, requires annual platform fees, and demands a minimum wallet recharge limit.
                </span>
              )}
            </div>
          </div>

          {/* Conditional Fields depending on system mode */}
          {isLocalMode ? (
            /* Local Production Configuration */
            <div style={{ animation: 'fadeIn 0.3s ease-in' }}>
              <div style={S.row}>
                <label style={S.label}>New Vendor Signup Credit (₹)</label>
                <input
                  style={S.input}
                  type="number"
                  min="0"
                  value={settings.free_credit_amount}
                  onChange={e => setSettings({ ...settings, free_credit_amount: e.target.value })}
                  onWheel={e => e.target.blur()}
                  required
                />
                <div style={S.helpText}>
                  The free credit balance seeded automatically to a new vendor wallet upon registration when system mode is set to Local Production.
                </div>
              </div>

              <div style={S.row}>
                <label style={S.label}>Request Credit Threshold (₹)</label>
                <input
                  style={S.input}
                  type="number"
                  min="0"
                  value={settings.request_credit_threshold || ''}
                  onChange={e => setSettings({ ...settings, request_credit_threshold: e.target.value })}
                  onWheel={e => e.target.blur()}
                  required
                />
                <div style={S.helpText}>
                  In sandbox mode, vendors with a wallet balance below this threshold can click "Request Credit" to ask for more testing funds.
                </div>
              </div>

              <div style={S.row}>
                <label style={S.label}>Auto Credit Amount (₹)</label>
                <input
                  style={S.input}
                  type="number"
                  min="0"
                  value={settings.auto_credit_amount || ''}
                  onChange={e => setSettings({ ...settings, auto_credit_amount: e.target.value })}
                  onWheel={e => e.target.blur()}
                  required
                />
                <div style={S.helpText}>
                  The wallet balance amount instantly credited to sandbox vendors upon clicking "Request Credit".
                </div>
              </div>
            </div>
          ) : (
            /* Live Production Configuration */
            <div style={{ animation: 'fadeIn 0.3s ease-in' }}>
              <div style={S.row}>
                <label style={S.label}>KYC / Platform Fee (₹)</label>
                <input
                  style={S.input}
                  type="number"
                  min="0"
                  value={settings.kyc_platform_fee}
                  onChange={e => setSettings({ ...settings, kyc_platform_fee: e.target.value })}
                  onWheel={e => e.target.blur()}
                  required
                />
                <div style={S.helpText}>
                  The annual fee required from vendors to activate KYC review and start receiving leads.
                </div>
              </div>

              <div style={S.row}>
                <label style={S.label}>Minimum Wallet Recharge Limit (₹)</label>
                <input
                  style={S.input}
                  type="number"
                  min="0"
                  value={settings.min_wallet_recharge}
                  onChange={e => setSettings({ ...settings, min_wallet_recharge: e.target.value })}
                  onWheel={e => e.target.blur()}
                  required
                />
                <div style={S.helpText}>
                  The minimum amount required for standard prepaid wallet top-ups.
                </div>
              </div>
            </div>
          )}

          {/* Save Button */}
          <div style={{ marginTop: 28, display: 'flex', justifyContent: 'flex-end' }}>
            <button
              style={{
                ...S.btn,
                opacity: saving ? 0.7 : 1,
                cursor: saving ? 'not-allowed' : 'pointer'
              }}
              type="submit"
              disabled={saving}
            >
              {saving ? 'Saving Changes...' : 'Save Settings'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
